import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { StoreResponse, TermBucketDetail, TermDetailOrder } from '@clarity/contract';
import { useTermBucketDetail } from '../../queries/index.js';
import { useScopeContextOptional } from '../../lib/scopeContext.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { dec, isOnboardingRequired, money, num, shortDate } from '../format.js';
import { AUDIENCE_LABEL, AudIcon, CapIcon, ScopeIcon } from './terms-shared.js';

/**
 * TermDetail (clarity-campaign-terms-economics task 9.3, Req 5.1) — the drill-
 * down for ONE terms bucket, rebuilt from the agreed mock
 * `brand/campaigns-terms-mock.html` ("VIEW · TERM DETAIL", the `#v-term` view).
 * The mock is the contract.
 *
 * It is a sibling route of the six workspace surfaces under `store/:storeId`
 * (like {@link CampaignDetail} / {@link OccurrenceDetail}), reached from the
 * Terms list's "Open term details →" affordance (task 6.2). The store comes from
 * the scope context; a passed `store` prop wins (tests mount it outside a
 * ScopeProvider). The `:bucketKey` route param is the bucket's signature `key`,
 * URL-ENCODED by the list link (it carries `%|:` separators) — React Router
 * hands it back decoded via `useParams`, so we pass it straight to the hook.
 *
 * The page shows, for the single bucket: a back-to-Terms link, the title
 * (percentage + signature chips, or "Unexplained") + the gating chips; FOUR
 * summary cards (Orders / restaurant-funded discount + co-funding / effective
 * discount % + "headline X% · cap Y" / avg basket); the observed-discount-spread
 * HISTOGRAM (a bar per bin, the declared-rate bin highlighted via
 * `.histo__fill.decl`, + an avg-observed-vs-declared callout); a "Who & how it
 * was funded" KV panel; and an "Orders in this term" table with keyboard-
 * operable filter chips (All / Pro / New customer / High conf / Low conf,
 * filtering the RETURNED page client-side) + a "Load more" page control.
 *
 * Reporting only — a pure READ of the server-computed {@link TermBucketDetail}.
 * `--font-num` on every figure, logical properties for RTL, WCAG 2.1 AA (the
 * table carries scope headers; confidence is conveyed by TEXT + a dot, never
 * colour alone; the filter chips are real buttons with `aria-pressed`).
 */

/** The stable sentinel key for the single Unexplained bucket (shared with the
 *  Terms list) — titled "Unexplained", never "0% off". */
const UNEXPLAINED_KEY = 'unexplained';

/** The page size for the orders slice (mirrors the server/core default). */
const PAGE_SIZE = 50;

export function TermDetail({ store: storeProp }: { store?: StoreResponse }) {
  const { clientId, storeId, bucketKey: rawBucketKey } = useParams();
  const navigate = useNavigate();

  const scope = useScopeContextOptional();
  const store = storeProp ?? scope?.store ?? null;
  // React Router decodes the path param, so `bucketKey` is the raw signature
  // string the terms axis keys on (the list link encoded it).
  const bucketKey = rawBucketKey ?? '';

  // How many pages we've asked the server to accumulate. "Load more" bumps this;
  // each page caches independently (the hook folds page into the key).
  const [page, setPage] = useState(1);

  const enabled = store != null && !isNotYetOnboarded(store) && bucketKey !== '';
  const query = useTermBucketDetail(
    store?.id ?? '',
    bucketKey,
    undefined,
    undefined,
    page,
    PAGE_SIZE,
    enabled,
  );
  const onboardingBlocked = isOnboardingRequired(query.error);

  const back = () => {
    if (clientId && storeId) navigate(`/c/${clientId}/store/${storeId}/campaigns`);
  };

  if (isOnboardingRequired(query.error)) {
    return (
      <Frame onBack={back}>
        <p className="muted">This branch isn’t onboarded yet.</p>
      </Frame>
    );
  }
  if (query.isLoading) {
    return (
      <Frame onBack={back}>
        <DetailLoading />
      </Frame>
    );
  }
  if ((query.isError && !onboardingBlocked) || !store) {
    return (
      <Frame onBack={back}>
        <p className="error" role="alert">
          Could not load this term.
        </p>
      </Frame>
    );
  }

  const detail: TermBucketDetail | null = query.data?.status === 200 ? query.data.body : null;
  if (!detail) {
    return (
      <Frame onBack={back}>
        <p className="error" role="alert">
          Could not load this term.
        </p>
      </Frame>
    );
  }

  const currency = store.profile?.currency ?? 'JOD';
  return (
    <Frame onBack={back}>
      <TermDetailLoaded
        detail={detail}
        currency={currency}
        page={page}
        onLoadMore={() => setPage((p) => p + 1)}
        loadingMore={query.isFetching}
      />
    </Frame>
  );
}

/** The page frame: a Back link to the Terms list + an accessible region. */
function Frame({ onBack, children }: { onBack: () => void; children: React.ReactNode }) {
  return (
    <section className="campaigns term-detail" aria-label="Term detail">
      <button type="button" className="back" onClick={onBack}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Terms
      </button>
      {children}
    </section>
  );
}

// ── The loaded term detail ──────────────────────────────────────────────────

function TermDetailLoaded({
  detail,
  currency,
  page,
  onLoadMore,
  loadingMore,
}: {
  detail: TermBucketDetail;
  currency: string;
  page: number;
  onLoadMore: () => void;
  loadingMore: boolean;
}) {
  const unexplained = isUnexplained(detail);
  const title = unexplained
    ? 'Unexplained'
    : `${num(detail.percentage)}% off${detail.itemScope.scoped ? ' · selected items' : ''}`;

  return (
    <>
      <header className="td-head">
        <div>
          <h2 className="td-head__title">{title}</h2>
          <div className="td-head__chips">
            {unexplained ? (
              <span className="gchip gchip--unm">no campaign / terms fit</span>
            ) : (
              <TermChips detail={detail} currency={currency} />
            )}
          </div>
        </div>
      </header>

      <SummaryCards detail={detail} currency={currency} unexplained={unexplained} />

      <div className="td-split">
        <section className="card td-panel" aria-label="Observed discount spread">
          <h3 className="td-panel__title">
            Observed discount spread
            <span className="td-panel__hint">
              {' '}
              · what each order actually showed — a cap or selected-items scope pulls it below the
              headline rate
            </span>
          </h3>
          <Histogram detail={detail} unexplained={unexplained} />
          <HistogramMark detail={detail} unexplained={unexplained} />
        </section>

        <section className="card td-panel" aria-label="Who and how it was funded">
          <h3 className="td-panel__title">Who &amp; how it was funded</h3>
          <FundingKvs detail={detail} currency={currency} />
        </section>
      </div>

      <OrdersPanel
        detail={detail}
        currency={currency}
        page={page}
        onLoadMore={onLoadMore}
        loadingMore={loadingMore}
        unexplained={unexplained}
      />
    </>
  );
}

/** The gating chips — the bucket's declared signature (percentage, item-scope,
 *  min, cap, audience). Reuses the shared icons + audience labels so the detail
 *  header draws the SAME chips as the list card. */
function TermChips({ detail, currency }: { detail: TermBucketDetail; currency: string }) {
  return (
    <>
      <span className="gchip">{num(detail.percentage)}% off</span>
      {detail.itemScope.scoped && (
        <span className="gchip gchip--scope">
          <ScopeIcon />
          selected · {detail.itemScope.label ?? 'items'}
        </span>
      )}
      {detail.minOrder !== null && (
        <span className="gchip">
          min {num(detail.minOrder)} {currency}
        </span>
      )}
      {detail.cap !== null && (
        <span className="gchip gchip--cap">
          <CapIcon />
          cap {money(detail.cap, currency)}
        </span>
      )}
      <span className="gchip gchip--aud">
        <AudIcon />
        {AUDIENCE_LABEL[detail.audience]}
      </span>
    </>
  );
}

/**
 * The four summary cards (the mock's `td-cards`): Orders, restaurant-funded
 * discount (+ co-funding sub), effective discount % (+ "headline X% · cap Y"
 * sub, or "observed, no declared rate" for Unexplained), avg basket. Every
 * figure is on `--font-num` via `.td-card__value`.
 */
function SummaryCards({
  detail,
  currency,
  unexplained,
}: {
  detail: TermBucketDetail;
  currency: string;
  unexplained: boolean;
}) {
  const { summary } = detail;
  const attributed = primaryAttributedLabel(detail.orders);

  const effSub = unexplained
    ? 'observed, no declared rate'
    : `headline ${num(detail.percentage)}% · ${
        detail.cap !== null ? `cap ${money(detail.cap, currency)}` : 'uncapped'
      }`;

  return (
    <div className="td-cards">
      <Card
        label="Orders"
        value={<span className="td-card__value num">{num(summary.orders)}</span>}
        sub={attributed ? `attributed to ${attributed}` : 'no campaign attributed'}
      />
      <Card
        label="Restaurant-funded discount"
        value={
          <span className="td-card__value num">
            {num(Math.round(summary.restaurantFunded))}
            <span className="td-card__unit">{currency}</span>
          </span>
        }
        sub={
          summary.platformFunded > 0
            ? `${num(Math.round(summary.platformFunded))} ${currency} platform co-funded`
            : 'no platform co-funding'
        }
      />
      <Card
        label="Effective discount"
        value={
          <span className="td-card__value num">
            {dec(summary.effectiveDiscountPct, 1)}
            <span className="td-card__unit">%</span>
          </span>
        }
        sub={effSub}
      />
      <Card
        label="Avg basket"
        value={
          <span className="td-card__value num">
            {dec(summary.avgBasket)}
            <span className="td-card__unit">{currency}</span>
          </span>
        }
        sub={`${num(Math.round(summary.subtotal))} ${currency} total subtotal`}
      />
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: React.ReactNode; sub: string }) {
  return (
    <div className="card td-card">
      <h4 className="td-card__label">{label}</h4>
      <div>{value}</div>
      <div className="td-card__sub">{sub}</div>
    </div>
  );
}

/**
 * The observed-% histogram (the mock's `td-histo`): one bar per fixed bin, width
 * ∝ the bin's share of the tallest bin. The bin the DECLARED rate lands in
 * (`detail.declBin`) is highlighted via `.histo__fill--decl`. Each bar is
 * labelled with its range AND its count in text (not bar length alone), and the
 * whole chart is a labelled `role="img"` naming every bin (WCAG 1.4.1 / 1.1.1).
 */
function Histogram({ detail, unexplained }: { detail: TermBucketDetail; unexplained: boolean }) {
  if (detail.histogram.length === 0) {
    return (
      <div className="empty" role="status">
        <p className="empty__sub muted">No observed-discount spread for this term in range.</p>
      </div>
    );
  }
  const max = Math.max(1, ...detail.histogram.map((b) => b.count));
  return (
    <div className="histo" role="img" aria-label={histogramAria(detail)}>
      {detail.histogram.map((bin) => {
        const width = Math.round((bin.count / max) * 100);
        const isDecl = !unexplained && bin.label === detail.declBin;
        return (
          <div className="histo__row" key={bin.label}>
            <span className="histo__lbl num" aria-hidden="true">
              {bin.label}
            </span>
            <span className="histo__track">
              <span
                className={`histo__fill${isDecl ? ' histo__fill--decl' : ''}`}
                style={{ inlineSize: `${width}%` }}
              />
            </span>
            <span className="histo__n num" aria-hidden="true">
              {num(bin.count)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The avg-observed-vs-declared callout under the histogram. For a declared
 *  bucket it names the observed effective rate against the headline and explains
 *  the gap; for Unexplained it states there is no declared rate. */
function HistogramMark({
  detail,
  unexplained,
}: {
  detail: TermBucketDetail;
  unexplained: boolean;
}) {
  return (
    <p className="histo__mark" role="note">
      <InfoIcon />
      {unexplained ? (
        <>
          Avg observed <b className="num">&nbsp;{dec(detail.summary.effectiveDiscountPct, 1)}%</b> —
          no declared rate (unexplained).
        </>
      ) : (
        <>
          Avg observed <b className="num">&nbsp;{dec(detail.summary.effectiveDiscountPct, 1)}%</b>
          &nbsp; vs declared <b className="num">&nbsp;{num(detail.percentage)}%</b> — the gap is the
          cap / selected-items dilution.
        </>
      )}
    </p>
  );
}

/** The "Who & how it was funded" KV panel (the mock's `td-kvs`): Pro %, new-
 *  customer %, platform co-funded, restaurant share, avg discount/order. Every
 *  value is on `--font-num`. */
function FundingKvs({ detail, currency }: { detail: TermBucketDetail; currency: string }) {
  const { summary } = detail;
  const share = (n: number) => (summary.orders > 0 ? Math.round((n / summary.orders) * 100) : 0);
  const rows: Array<[string, React.ReactNode]> = [
    [
      'Pro (TPRO) orders',
      <>
        {num(summary.pro)} <span className="kvrow__mut">{num(share(summary.pro))}%</span>
      </>,
    ],
    [
      'New-customer orders',
      <>
        {num(summary.newCustomer)}{' '}
        <span className="kvrow__mut">{num(share(summary.newCustomer))}%</span>
      </>,
    ],
    ['Platform co-funded', `${dec(summary.platformFunded)} ${currency}`],
    ['Restaurant share', `${dec(summary.restaurantFunded)} ${currency}`],
    ['Avg discount / order', `${dec(summary.avgDiscountPerOrder)} ${currency}`],
  ];
  return (
    <dl className="kvs">
      {rows.map(([k, v]) => (
        <div className="kvrow" key={k}>
          <dt className="kvrow__k">{k}</dt>
          <dd className="kvrow__v num">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Orders table + filter chips + pagination ──────────────────────────────────

type OrderFilter = 'all' | 'pro' | 'nc' | 'high' | 'low';

const ORDER_FILTERS: Array<[OrderFilter, string]> = [
  ['all', 'All'],
  ['pro', 'Pro'],
  ['nc', 'New customer'],
  ['high', 'High conf'],
  ['low', 'Low conf'],
];

/** True if the order passes the active filter (applied to the RETURNED page —
 *  the detail is scoped to one bucket, so the page is small and client-side
 *  filtering is honest about what it narrows: the loaded rows). */
function matchesFilter(order: TermDetailOrder, filter: OrderFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'pro':
      return order.isPro;
    case 'nc':
      return order.newCustomer;
    case 'high':
      return order.confidence === 'high';
    case 'low':
      return order.confidence === 'low';
  }
}

function OrdersPanel({
  detail,
  currency,
  page,
  onLoadMore,
  loadingMore,
  unexplained,
}: {
  detail: TermBucketDetail;
  currency: string;
  page: number;
  onLoadMore: () => void;
  loadingMore: boolean;
  unexplained: boolean;
}) {
  const [filter, setFilter] = useState<OrderFilter>('all');
  const rows = useMemo(
    () => detail.orders.filter((o) => matchesFilter(o, filter)),
    [detail.orders, filter],
  );
  const counts = useMemo(() => {
    const by = (f: OrderFilter) => detail.orders.filter((o) => matchesFilter(o, f)).length;
    return {
      all: by('all'),
      pro: by('pro'),
      nc: by('nc'),
      high: by('high'),
      low: by('low'),
    } as Record<OrderFilter, number>;
  }, [detail.orders]);

  // The returned page covers `page × pageSize` orders at most; more remain while
  // that is below the bucket's full `total`.
  const hasMore = page * detail.pageSize < detail.total;

  return (
    <section className="card td-panel" aria-label="Orders in this term">
      <h3 className="td-panel__title">
        Orders in this term
        <span className="td-panel__hint">
          {' '}
          · {num(detail.total)} order{detail.total === 1 ? '' : 's'} carry these terms
          {detail.total > detail.orders.length ? '; showing the newest' : ''}
        </span>
      </h3>

      <div className="ord-filter" role="group" aria-label="Filter orders">
        {ORDER_FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className="ord-chip"
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
            <span className="ord-chip__c num">{num(counts[key])}</span>
          </button>
        ))}
      </div>

      <div className="td-tablewrap">
        <table className="data" aria-label="Orders in this term">
          <thead>
            <tr>
              <th scope="col">Order</th>
              <th scope="col">Placed</th>
              <th scope="col" className="num-col">
                Subtotal
              </th>
              <th scope="col" className="num-col">
                Discount
              </th>
              <th scope="col" className="num-col">
                Observed %
              </th>
              <th scope="col">Attributed</th>
              <th scope="col">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="td-ord-empty muted">
                  No orders match this filter.
                </td>
              </tr>
            ) : (
              rows.map((order) => (
                <OrderRow
                  key={order.orderId}
                  order={order}
                  currency={currency}
                  unexplained={unexplained}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="td-loadmore">
          <button
            type="button"
            className="btn ghost"
            onClick={onLoadMore}
            disabled={loadingMore}
            aria-busy={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

/** One order row. The external id + date lead; subtotal/discount/observed are on
 *  `--font-num`; the attributed cell names the campaign (or "unexplained") with
 *  the Pro/New flags; confidence is a dot + its label TEXT (never colour alone). */
function OrderRow({
  order,
  currency,
  unexplained,
}: {
  order: TermDetailOrder;
  currency: string;
  unexplained: boolean;
}) {
  const attributed = unexplained || order.campaignId === null;
  return (
    <tr>
      <td className="num td-ord-id">{order.externalOrderId}</td>
      <td className="num td-ord-when">{shortDate(order.placedAt)}</td>
      <td className="num-col">{money(order.subtotal, currency)}</td>
      <td className="num-col">{money(order.discount, currency)}</td>
      <td className="num-col">{dec(order.observedPct, 0)}%</td>
      <td>
        {attributed ? (
          <span className="pill pill--unm">unexplained</span>
        ) : (
          <span className="pill pill--matched">{order.attributedLabel}</span>
        )}
        {order.isPro && <span className="flag flag--pro">Pro</span>}
        {order.newCustomer && <span className="flag flag--nc">New</span>}
      </td>
      <td>
        <ConfidenceDot confidence={order.confidence} />
      </td>
    </tr>
  );
}

/** The confidence indicator: a coloured dot PLUS its level in text, so it reads
 *  without colour (WCAG 1.4.1). Null confidence (an unmatched order) is an
 *  em-dash. */
function ConfidenceDot({ confidence }: { confidence: TermDetailOrder['confidence'] }) {
  if (confidence === null) return <span className="conf-dot__none">—</span>;
  const label = confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low';
  return (
    <span className={`conf-dot conf-dot--${confidence}`}>
      <i aria-hidden="true" />
      {label}
    </span>
  );
}

function DetailLoading() {
  return (
    <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading term…</span>
      <span className="sk" style={{ inlineSize: '55%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '85%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '70%' }} aria-hidden="true" />
    </div>
  );
}

// ── Pure helpers + icon ────────────────────────────────────────────────────────

/** True for the single Unexplained bucket (same sentinel the Terms list uses). */
function isUnexplained(detail: TermBucketDetail): boolean {
  return detail.key === UNEXPLAINED_KEY;
}

/**
 * The primary attributed campaign label for the "Orders" card sub — the most
 * common non-"Unexplained" `attributedLabel` across the returned page. Returns
 * null when nothing is attributed (every row unexplained, or no rows), so the
 * card reads "no campaign attributed" rather than inventing a deal.
 */
function primaryAttributedLabel(orders: TermDetailOrder[]): string | null {
  const tally = new Map<string, number>();
  for (const o of orders) {
    if (o.campaignId === null || o.attributedLabel.toLowerCase() === 'unexplained') continue;
    tally.set(o.attributedLabel, (tally.get(o.attributedLabel) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [label, n] of tally) {
    if (n > bestN) {
      best = label;
      bestN = n;
    }
  }
  return best;
}

/** The histogram's accessible description — names every bin + its count, so the
 *  distribution is available to a screen reader, not just the bars. */
function histogramAria(detail: TermBucketDetail): string {
  const parts = detail.histogram.map((b) => `${b.label} ${b.count}`);
  return `Observed discount spread: ${parts.join(', ')}`;
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}
